/**
 * Client-side settings persisted to localStorage.
 * LLM config, web search credentials (per-provider), and context-window
 * control. Also exports model-preset definitions used by the settings UI.
 */

import { ContextConfig, LLMConfig } from "./types";

const KEY = "horsir:config:v1";
const LEGACY_KEY = "office-ai-addin:config:v1";

const DEFAULT: LLMConfig = {
  baseUrl: "https://api.openai.com/v1",
  apiKey: "",
  model: "gpt-4o-mini",
  temperature: 0.7,
};

export interface Preset {
  label: string;
  baseUrl: string;
  model: string;
  note?: string;
  /** Typical context window in tokens for this preset's default model. */
  contextTokens?: number;
}

/** Common OpenAI-compatible endpoints. All speak /v1/chat/completions. */
export const PRESETS: Preset[] = [
  { label: "OpenAI", baseUrl: "https://api.openai.com/v1", model: "gpt-4o-mini", contextTokens: 128000 },
  { label: "DeepSeek", baseUrl: "https://api.deepseek.com/v1", model: "deepseek-v4-flash", contextTokens: 131072 },
  { label: "Moonshot (Kimi)", baseUrl: "https://api.moonshot.cn/v1", model: "moonshot-v1-8k", contextTokens: 8192 },
  { label: "阿里通义 (DashScope)", baseUrl: "https://dashscope.aliyuncs.com/compatible-mode/v1", model: "qwen-plus", contextTokens: 131072 },
  { label: "智谱 GLM", baseUrl: "https://open.bigmodel.cn/api/paas/v4", model: "glm-4-flash", contextTokens: 131072 },
  { label: "Ollama (本地)", baseUrl: "http://localhost:11434/v1", model: "llama3.1", note: "需开启 CORS：OLLAMA_ORIGINS=* ollama serve", contextTokens: 131072 },
  { label: "LM Studio (本地)", baseUrl: "http://localhost:1234/v1", model: "local-model", contextTokens: 65536 },
];

export function loadConfig(): LLMConfig {
  try {
    const raw = window.localStorage.getItem(KEY) ?? window.localStorage.getItem(LEGACY_KEY);
    if (!raw) return { ...DEFAULT };
    return { ...DEFAULT, ...(JSON.parse(raw) as Partial<LLMConfig>) };
  } catch {
    return { ...DEFAULT };
  }
}

export function saveConfig(cfg: LLMConfig): void {
  window.localStorage.setItem(KEY, JSON.stringify(cfg));
  try {
    window.localStorage.removeItem(LEGACY_KEY);
  } catch {
    /* ignore */
  }
}

export function isConfigured(cfg: LLMConfig): boolean {
  return Boolean(cfg.baseUrl && cfg.model);
}

// ---------------------------------------------------------------------------
// Web search configuration (used by the server-side agent)
// ---------------------------------------------------------------------------
export type SearchProvider = "glm" | "tavily" | "searxng" | "bocha" | "baidu";

/** Per-provider saved credentials so users can keep separate keys/endpoints
 *  for every engine and switch between them without re-typing. */
export type SearchCredentials = Partial<Record<SearchProvider, { apiKey: string; endpoint: string }>>;

export interface SearchConfig {
  provider: SearchProvider;
  apiKey: string;
  endpoint: string;
  maxResults: number;
  /** Per-provider credentials, keyed by provider id. The legacy flat
   *  `apiKey` / `endpoint` fields mirror the *active* provider's values for
   *  backward compatibility with the server proxy and older exports. */
  credentials?: SearchCredentials;
}

const SEARCH_KEY = "horsir:search:v1";
const SEARCH_DEFAULT: SearchConfig = { provider: "baidu", apiKey: "", endpoint: "", maxResults: 5 };

export interface SearchPreset {
  value: SearchProvider;
  label: string;
  note: string;
  needsKey: boolean;
  needsEndpoint: boolean;
  /** Where users can sign up / create the API key (or, for self-hosted
   *  providers, the deployment docs) — shown as a guidance link in settings. */
  keyUrl?: string;
  i18nLabelKey?: string;
  i18nNoteKey?: string;
}

export const SEARCH_PRESETS: SearchPreset[] = [
  { value: "baidu", label: "百度 AI 搜索（需 API Key）", note: "百度千帆 AI 搜索，国内直连，质量高，需填写 BCE API Key（sk-... 或 bce-v3/...）。", needsKey: true, needsEndpoint: false, keyUrl: "https://console.bce.baidu.com/iam/#/iam/apikey/list", i18nLabelKey: "settings.searchBaidu", i18nNoteKey: "settings.searchBaiduNote" },
  { value: "bocha", label: "博查 Bocha（需 API Key）", note: "国内 AI 搜索 API，中英文俱佳，需填写博查 API Key。", needsKey: true, needsEndpoint: false, keyUrl: "https://open.bochaai.com/", i18nLabelKey: "settings.searchBocha", i18nNoteKey: "settings.searchBochaNote" },
  { value: "glm", label: "智谱 GLM（需 API Key）", note: "智谱 web_search，中英文俱佳，需填写 GLM API Key。", needsKey: true, needsEndpoint: false, keyUrl: "https://open.bigmodel.cn/usercenter/apikeys", i18nLabelKey: "settings.searchGlm", i18nNoteKey: "settings.searchGlmNote" },
  { value: "tavily", label: "Tavily（需 API Key）", note: "AI 搜索，有免费额度，需填写 API Key。", needsKey: true, needsEndpoint: false, keyUrl: "https://app.tavily.com/", i18nLabelKey: "settings.searchTavily", i18nNoteKey: "settings.searchTavilyNote" },
  { value: "searxng", label: "SearXNG（自建实例）", note: "填写你的 SearXNG 实例地址，完全免费、自托管。", needsKey: false, needsEndpoint: true, keyUrl: "https://docs.searxng.org/", i18nLabelKey: "settings.searchSearxng", i18nNoteKey: "settings.searchSearxngNote" },
];

export function loadSearch(): SearchConfig {
  try {
    const raw = window.localStorage.getItem(SEARCH_KEY);
    if (!raw) return { ...SEARCH_DEFAULT };
    const parsed = JSON.parse(raw) as Partial<SearchConfig>;
    const cfg: SearchConfig = { ...SEARCH_DEFAULT, ...parsed };
    // Migrate removed providers (jina/wikipedia) → fall back to default so
    // existing users don't get stuck on an unavailable engine.
    const VALID: SearchProvider[] = SEARCH_PRESETS.map((p) => p.value);
    if (cfg.provider && !VALID.includes(cfg.provider)) cfg.provider = SEARCH_DEFAULT.provider;
    // Migrate legacy single-key configs into the credentials map under the
    // active provider, so switching providers no longer overwrites the key.
    if (!cfg.credentials) cfg.credentials = {};
    if (cfg.apiKey || cfg.endpoint) {
      const active = cfg.provider || SEARCH_DEFAULT.provider;
      const existing = cfg.credentials[active];
      if (!existing) cfg.credentials[active] = { apiKey: cfg.apiKey, endpoint: cfg.endpoint };
    }
    return cfg;
  } catch {
    return { ...SEARCH_DEFAULT };
  }
}

export function saveSearch(cfg: SearchConfig): void {
  window.localStorage.setItem(SEARCH_KEY, JSON.stringify(cfg));
}

/** Save the given key/endpoint under a specific provider in the credentials
 *  map. Returns a *new* credentials object (immutable update). */
export function putSearchCredential(
  creds: SearchCredentials | undefined,
  provider: SearchProvider,
  apiKey: string,
  endpoint: string,
): SearchCredentials {
  return { ...(creds ?? {}), [provider]: { apiKey, endpoint } };
}

/** Read saved credentials for a provider, falling back to empty strings. */
export function getSearchCredential(
  creds: SearchCredentials | undefined,
  provider: SearchProvider,
): { apiKey: string; endpoint: string } {
  return creds?.[provider] ?? { apiKey: "", endpoint: "" };
}

// ---------------------------------------------------------------------------
// Context window control (used by the server-side agent to prevent overflow)
// ---------------------------------------------------------------------------
const CONTEXT_KEY = "horsir:context:v1";
const CONTEXT_DEFAULT: ContextConfig = { maxTokens: 65536, summaryThreshold: 0.8 };

export function loadContextConfig(): ContextConfig {
  try {
    const raw = window.localStorage.getItem(CONTEXT_KEY);
    if (!raw) return { ...CONTEXT_DEFAULT };
    return { ...CONTEXT_DEFAULT, ...(JSON.parse(raw) as Partial<ContextConfig>) };
  } catch {
    return { ...CONTEXT_DEFAULT };
  }
}

export function saveContextConfig(cfg: ContextConfig): void {
  window.localStorage.setItem(CONTEXT_KEY, JSON.stringify(cfg));
}
